import { formatDateBR } from '../../utils/dateUtils';
import { DocumentArrowDownIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import type { PaginatedResponse } from '../../services/resultados.service';
import { useQuery } from '@tanstack/react-query';
import { resultadosService, type Resultado } from '../../services/resultados.service';
import { useToast } from '../Toast';

interface ResultadosLaudosTabProps {
  colaboradorId: number;
}

const statusConfig = {
  aprovado: {
    label: 'Aprovado / Apto',
    color: 'bg-green-100 text-green-800',
    icon: CheckCircleIcon,
  },
  reprovado: {
    label: 'Reprovado / Inapto',
    color: 'bg-red-100 text-red-800',
    icon: XCircleIcon,
  },
  laudo_emitido: {
    label: 'Laudo Emitido',
    color: 'bg-sky-100 text-sky-800',
    icon: CheckCircleIcon,
  },
  pendente: {
    label: 'Pendente',
    color: 'bg-yellow-100 text-yellow-800',
    icon: ClockIcon,
  },
};

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

export default function ResultadosLaudosTab({ colaboradorId }: ResultadosLaudosTabProps) {
  const toast = useToast();

  // Buscar resultados da API
  const { data, isLoading, error } = useQuery<PaginatedResponse<Resultado>>({
    queryKey: ['resultados', colaboradorId],
    queryFn: () => resultadosService.getResultadosPorColaborador(colaboradorId),
    retry: 2,
  });

  const resultados = data?.data || [];

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return formatDateBR(dateString);
    } catch {
      return dateString;
    }
  };

  const handleDownload = async (resultadoId: number) => {
    try {
      const blob = await resultadosService.downloadResultado(resultadoId);
      const url = window.URL.createObjectURL(blob);
      const opened = window.open(url, '_blank');
      if (!opened) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `laudo_${resultadoId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 60000);
      toast.success('Download iniciado');
    } catch (error) {
      console.error('❌ Erro ao fazer download:', error);
      toast.error('Erro ao fazer download do arquivo. Tente novamente.');
    }
  };

  const aprovados = resultados.filter(r => r.status === 'aprovado').length;
  const reprovados = resultados.filter(r => r.status === 'reprovado').length;
  const laudosEmitidos = resultados.filter(r => r.status === 'laudo_emitido').length;
  const pendentes = resultados.filter(r => r.status === 'pendente').length;

  if (isLoading) {
    return (
      <div className="py-4">
        <div className="text-center py-8 text-gray-500">
          Carregando resultados...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4">
        <div className="text-center py-8 text-red-500">
          Erro ao carregar resultados. Tente novamente.
        </div>
      </div>
    );
  }

  return (
    <div className="py-4">
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--h1Color)' }}>
          Resultados e Laudos
        </h3>

        {/* Estatísticas */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="card text-center">
            <div className="text-2xl font-bold" style={{ color: 'var(--h1Color)' }}>
              {resultados.length}
            </div>
            <div className="text-gray-600 text-sm">Total de Exames</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-green-600">{aprovados}</div>
            <div className="text-gray-600 text-sm">Aprovados</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-red-600">{reprovados}</div>
            <div className="text-gray-600 text-sm">Reprovados</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-sky-600">{laudosEmitidos}</div>
            <div className="text-gray-600 text-sm">Laudos Emitidos</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-yellow-600">{pendentes}</div>
            <div className="text-gray-600 text-sm">Pendentes</div>
          </div>
        </div>
      </div>

      {/* Lista de Resultados */}
      <div className="space-y-4">
        {resultados.map((resultado) => {
          const status = statusConfig[resultado.status as keyof typeof statusConfig] || statusConfig.pendente;
          const StatusIcon = status.icon;

          return (
            <div key={resultado.id} className="card">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <DocumentArrowDownIcon className="w-5 h-5 text-gray-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-gray-900">{resultado.exame}</h4>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {status.label}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Data de Realização:</span> {formatDate(resultado.data_realizacao)}
                        </div>
                        <div>
                          <span className="font-medium">Data de Emissão:</span> {formatDate(resultado.data_emissao)}
                        </div>
                        {resultado.medico && resultado.crm && (
                          <div>
                            <span className="font-medium">Médico:</span> {resultado.medico} - {resultado.crm}
                          </div>
                        )}
                        <div>
                          <span className="font-medium">Clínica:</span> {resultado.clinica}
                        </div>
                      </div>
                      {resultado.observacoes && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-700">
                            <span className="font-medium">Observações:</span> {resultado.observacoes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {resultado.arquivo_url && (
                  <div>
                    <button
                      onClick={() => handleDownload(resultado.id)}
                      className="btn-primary text-sm flex items-center gap-2"
                    >
                      <DocumentArrowDownIcon className="w-4 h-4" />
                      Baixar Laudo
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {resultados.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          Nenhum resultado encontrado
        </div>
      )}
    </div>
  );
}
